import { Box, Typography } from "@outschool/backpack";
import { isLiveOneTime } from "@outschool/business-rules";
import { Activity as ActivityType } from "@outschool/gql-backend-generated";
import { ActivityListing_ListingFragment } from "@outschool/gql-frontend-generated";
import { TFunction } from "@outschool/localization";
import { plural } from "@outschool/text";
import {
  useIsActivitySavedByCurrentUser,
  useShowCredits
} from "@outschool/ui-components-website";
import { TrackingProperties } from "@outschool/ui-legacy-component-library";
import { Screen } from "@outschool/ui-utils";
import capitalize from "lodash/capitalize";
import React from "react";

import * as Activity from "../../../shared/Activity";
import * as Section from "../../../shared/Section";
import { DesktopListing } from "./DesktopListing";
import { MobileListing, MobileListingOverrideVariant } from "./MobileListing";

export function Listings({
  children,
  title
}: React.PropsWithChildren<{ title?: string; className?: string }>) {
  if (!React.Children.map(children, c => c)?.length) {
    return null;
  }

  return (
    <Box>
      {!!title && (
        <Box
          flex
          sx={{
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "space-between"
          }}
        >
          <Typography variant="h3">{title}</Typography>
        </Box>
      )}
      <Box>{children}</Box>
    </Box>
  );
}

type ScheduleTextActivity = Pick<
  ActivityType,
  | "duration_weeks"
  | "weekly_meetings"
  | "is_ongoing_weekly"
  | "isClub"
  | "isSelfPaced"
> & { hasTeacherSchedule?: boolean };

export function scheduleText(
  activity: ScheduleTextActivity,
  t?: TFunction
): [string, string?] {
  if (!t) {
    return scheduleTextNoT(activity);
  }
  const { duration_weeks: durationWeeks, weekly_meetings: weeklyMeetings } =
    activity;
  let frequencyText: string | undefined = undefined;
  let durationText: string | undefined = undefined;
  if (!!activity.is_ongoing_weekly) {
    switch (weeklyMeetings) {
      case 1:
        frequencyText = t`Meets once per week`;
        break;
      case 2:
        frequencyText = t`Meets twice per week`;
        break;
      default:
        // @ts-ignore TS(2769): No overload matches this call.
        frequencyText = t("Meets {{numWeeklyMeetings}} times per week", {
          numWeeklyMeetings: weeklyMeetings,
          count: weeklyMeetings || 0
        });
    }
  } else if (activity.isSelfPaced === true) {
    frequencyText = t("Self-paced");
  } else if (Activity.isFlexSchedule(activity) === true) {
    // @ts-ignore TS(2769): No overload matches this call.
    frequencyText = t("Flexible for {{numDurationWeeks}} week", {
      numDurationWeeks: durationWeeks,
      count: durationWeeks || 0,
      defaultValue_plural: "Flexible for {{numDurationWeeks}} weeks"
    });
  } else if (isLiveOneTime(activity) === true) {
    frequencyText = t`Meets once`;
  } else if (durationWeeks === 1) {
    switch (weeklyMeetings) {
      case 1:
        frequencyText = t`Meets once`;
        break;
      case 2:
        frequencyText = t`Meets twice `;
        break;
      default:
        // @ts-ignore TS(2769): No overload matches this call.
        frequencyText = t("Meets {{numWeeklyMeetings}} times", {
          numWeeklyMeetings: weeklyMeetings,
          count: weeklyMeetings || 0
        });
    }
    durationText = t`Over 1 week`;
  } else if (activity.hasTeacherSchedule) {
    return [t`Meets on demand`, undefined];
  } else {
    switch (weeklyMeetings) {
      case 1:
        frequencyText = t`Meets once per week`;
        break;
      case 2:
        frequencyText = t`Meets twice per week`;
        break;
      default:
        // @ts-ignore TS(2769): No overload matches this call.
        frequencyText = t("Meets {{numWeeklyMeetings}} times per week", {
          numWeeklyMeetings: weeklyMeetings,
          count: weeklyMeetings || 0
        });
    }
    // @ts-ignore TS(2769): No overload matches this call.
    durationText = t("Over {{numDurationWeeks}} week", {
      numDurationWeeks: durationWeeks,
      count: durationWeeks || 0,
      defaultValue_plural: "Over {{numDurationWeeks}} weeks"
    });
  }
  // @ts-ignore TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
  return [frequencyText, durationText];
}

function scheduleTextNoT(activity: ScheduleTextActivity): [string, string?] {
  const frequencyLanguage = (frequency: number): string => {
    if (frequency === 1) {
      return "once";
    } else if (frequency === 2) {
      return "twice";
    } else {
      return `${frequency} times`;
    }
  };
  const { duration_weeks: durationWeeks, weekly_meetings: weeklyMeetings } =
    activity;
  let frequencyText: string | undefined = undefined;
  let durationText: string | undefined = undefined;
  if (!!activity.is_ongoing_weekly) {
    // @ts-ignore TS(2345): Argument of type 'Maybe<number>' is not assignable... Remove this comment to see the full error message
    frequencyText = `meets ${frequencyLanguage(weeklyMeetings)} per week`;
  } else if (activity.isSelfPaced === true) {
    frequencyText = "self-paced";
  } else if (Activity.isFlexSchedule(activity) === true) {
    frequencyText = `flexible for ${durationWeeks} ${plural(
      "week",
      // @ts-ignore TS(2345): Argument of type 'Maybe<number>' is not assignable... Remove this comment to see the full error message
      durationWeeks
    )}`;
  } else if (isLiveOneTime(activity) === true) {
    frequencyText = "meets once";
  } else if (durationWeeks === 1) {
    // @ts-ignore TS(2345): Argument of type 'Maybe<number>' is not assignable... Remove this comment to see the full error message
    frequencyText = `meets ${frequencyLanguage(weeklyMeetings)}`;
    durationText = `over 1 week`;
  } else if (activity.hasTeacherSchedule) {
    frequencyText = `meets on demand`;
  } else {
    // @ts-ignore TS(2345): Argument of type 'Maybe<number>' is not assignable... Remove this comment to see the full error message
    frequencyText = `${frequencyLanguage(weeklyMeetings)} per week`;
    // @ts-ignore TS(2345): Argument of type 'Maybe<number>' is not assignable... Remove this comment to see the full error message
    durationText = `over ${durationWeeks} ${plural("week", durationWeeks)}`;
  }
  return [capitalize(frequencyText), durationText && capitalize(durationText)];
}

function activityTrackingProps(listing: ActivityListing_ListingFragment) {
  return {
    activity_uid: listing.activity?.uid,
    activity_age_max: listing.activity?.age_max,
    activity_age_min: listing.activity?.age_min,
    activity_average_star_rating: listing.activity?.averageStarRating,
    activity_duration_minutes: listing.activity?.duration_minutes,
    activity_duration_weeks: listing.activity?.duration_weeks,
    activity_is_club: listing.activity?.isClub?.toString(),
    activity_is_ongoing_weekly: listing.activity?.is_ongoing_weekly?.toString(),
    activity_price_cents: listing.activity?.price_cents,
    activity_review_count: listing.activity?.reviewCount,
    activity_weekly_meetings: listing.activity?.weekly_meetings,
    first_section_filled_space_count: listing.nextSection?.filledSpaceCount,
    first_section_size_max: listing.nextSection?.size_max,
    first_section_start_time: listing.nextSection?.start_time,
    leader_average_activity_star_rating:
      listing.leader?.averageActivityStarRating,
    leader_review_count: listing.leader?.reviewCount,
    section_uid: listing.nextSection?.uid
  };
}

export type ActivityListingProps = {
  listing: ActivityListing_ListingFragment;
  sectionSearchFilters?: Section.SectionSearchFilters;
  trackingProperties?: TrackingProperties;
  disabled?: boolean;
  openInNewTab?: boolean;
  openInSSRForLoggedOutUsers?: boolean;
  listingIndex?: number;
  variant?: "cdpAsClp" | "Share" | "Review" | "Planner" | "ShoppingCart";
  pageLocation: string;
  listingSectionOverride?: React.ReactNode;
  showFundingBadge?: boolean;
};

/**
 * Displays a single activity's row on the listing page.
 * Props expected: history, brief, activity.
 */
export const ActivityListing = ({
  listing,
  listingIndex,
  sectionSearchFilters,
  openInSSRForLoggedOutUsers,
  variant,
  trackingProperties = {},
  disabled = false,
  openInNewTab = false,
  pageLocation,
  listingSectionOverride,
  showFundingBadge
}: ActivityListingProps) => {
  const isActivitySavedByCurrentUser = useIsActivitySavedByCurrentUser(
    listing.activity
  );
  const { nextSection, activity, leader } = listing;
  const showCredits = useShowCredits();

  const isMobile = Screen.useIsMobile();
  const insertionId = listing.insertionId;

  const additionalTrackingProperties = activityTrackingProps(listing);

  const moddedVariant = variant;

  if (isMobile) {
    if (moddedVariant === "Review") {
      return (
        <MobileListingOverrideVariant
          activity={activity}
          leader={leader}
          sectionSearchFilters={sectionSearchFilters}
          trackingProperties={{
            ...trackingProperties,
            ...additionalTrackingProperties
          }}
          disabled={disabled}
          insertionId={insertionId}
          openInSSRForLoggedOutUsers={openInSSRForLoggedOutUsers}
          listingIndex={listingIndex}
          pageLocation={pageLocation}
          variant={moddedVariant}
          listingSectionOverride={listingSectionOverride}
        />
      );
    } else {
      return (
        <MobileListing
          activity={activity}
          nextSection={nextSection}
          leader={leader}
          sectionSearchFilters={sectionSearchFilters}
          trackingProperties={{
            ...trackingProperties,
            ...additionalTrackingProperties
          }}
          disabled={disabled}
          insertionId={insertionId}
          openInSSRForLoggedOutUsers={openInSSRForLoggedOutUsers}
          listingIndex={listingIndex}
          pageLocation={pageLocation}
          showCredits={showCredits}
          showFundingBadge={showFundingBadge}
        />
      );
    }
  } else {
    return (
      <DesktopListing
        activity={activity}
        nextSection={nextSection}
        leader={leader}
        isActivitySavedByCurrentUser={isActivitySavedByCurrentUser}
        trackingProperties={{
          ...trackingProperties,
          ...additionalTrackingProperties
        }}
        disabled={disabled}
        openInNewTab={openInNewTab}
        insertionId={insertionId}
        listingIndex={listingIndex}
        variant={moddedVariant}
        openInSSRForLoggedOutUsers={openInSSRForLoggedOutUsers}
        pageLocation={pageLocation}
        listingSectionOverride={listingSectionOverride}
        showCredits={showCredits}
        showFundingBadge={showFundingBadge}
      />
    );
  }
};
